import { Icon, ProgressRing, type IconName } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import type { TranslationKey } from '@/i18n-key';
import type { ObjectivesSummary } from '@/utils/objectivesSummary';

import { formatPercent } from '@/utils/numberFormat';
import { UNKNOWN_FIGURE } from '@/utils/objectivesSummary';
import { OBJECTIVE_STATUS_LABEL_KEYS } from '@/utils/objectiveStatus';

/** The overall ring's diameter and stroke; the badge under it is centred on the same box. */
const RING_SIZE_PX = 186;
const RING_STROKE_PX = 10;

interface HeroStat {
    key: 'accomplished' | 'onTrack' | 'atRisk' | 'overdue';
    labelKey: TranslationKey;
    icon: IconName;
    value: number;
    /** The icon and the soft tile behind it speak in the same colour. */
    iconClassName: string;
    tileClassName: string;
}

interface ObjectivesHeroProps {
    summary: ObjectivesSummary;
    /** False until the objectives are backed by a server answer: every figure then reads as a dash. */
    isSummaryKnown: boolean;
    /** The company's name in the reader's language; the title stays generic without it. */
    companyName: string;
    /** The company's strategic vision; the handwritten line is left out when it is empty. */
    strategicVision: string;
}

/** Two digits, the way the headline counts read: 01, 02, 12. */
const formatCount = (count: number) => String(count).padStart(2, '0');

/**
 * The top of the portal's Objectives tab: what the employee's objectives serve (the company and its
 * vision), their overall progression as a ring, and the count of each state underneath.
 */
export const ObjectivesHero = ({
    summary,
    isSummaryKnown,
    companyName,
    strategicVision,
}: ObjectivesHeroProps) => {
    // The untyped `t`: the generated key list only knows the plural forms (`_one`, `_other`) of the
    // in-progress count, while i18next picks between them from the base key.
    const { t, i18n } = useTranslation();

    const stats: HeroStat[] = [
        {
            key: 'accomplished',
            labelKey: 'employeePortal.objectives.hero.accomplished',
            icon: 'essential-tick-circle',
            value: summary.achieved,
            iconClassName: 'text-primary',
            tileClassName: 'from-primary/25 to-primary/5',
        },
        {
            key: 'onTrack',
            labelKey: OBJECTIVE_STATUS_LABEL_KEYS.onTrack,
            icon: 'business-trend-up',
            value: summary.onTrack,
            iconClassName: 'text-violet',
            tileClassName: 'from-violet/25 to-violet/5',
        },
        {
            key: 'atRisk',
            labelKey: OBJECTIVE_STATUS_LABEL_KEYS.atRisk,
            icon: 'essential-warning-2',
            value: summary.atRisk,
            iconClassName: 'text-warning',
            tileClassName: 'from-warning/25 to-warning/5',
        },
        {
            key: 'overdue',
            labelKey: OBJECTIVE_STATUS_LABEL_KEYS.overdue,
            icon: 'essential-danger',
            value: summary.overdue,
            iconClassName: 'text-error',
            tileClassName: 'from-error/20 to-error/5',
        },
    ];

    const overallLabel = t('employeePortal.objectives.hero.overallProgression');

    return (
        <section
            aria-labelledby={'employee-portal-objectives-hero-title'}
            className={
                'shadow-portal-hero from-portal-hero-start via-portal-hero-mid to-portal-hero-end relative overflow-hidden rounded-[32px] border border-white/40 bg-linear-to-br'
            }
            data-testid={'employee-portal-objectives-hero'}
        >
            {/* Two soft glows light the night from opposite corners. */}
            <div
                aria-hidden
                className={
                    'bg-primary/25 pointer-events-none absolute -top-24 -right-16 size-[420px] rounded-full blur-3xl'
                }
            />
            <div
                aria-hidden
                className={
                    'bg-violet/40 pointer-events-none absolute -bottom-32 -left-10 size-[380px] rounded-full blur-3xl'
                }
            />

            <div
                className={
                    'relative flex max-lg:flex-col max-lg:gap-8 max-lg:p-6 lg:flex-row lg:items-center lg:gap-14 lg:p-12'
                }
            >
                <div className={'min-w-0 flex-1'}>
                    <span
                        className={
                            'inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/15 px-3 py-1 text-[11px] font-semibold tracking-widest text-white/90 uppercase'
                        }
                    >
                        <Icon
                            name={'lucide-sparkles'}
                            size={14}
                            color={'inherit'}
                            ariaHidden
                        />

                        {t('employeePortal.objectives.hero.badge')}
                    </span>

                    <h2
                        id={'employee-portal-objectives-hero-title'}
                        className={
                            'mt-4 leading-tight font-medium text-white max-lg:text-[24px] lg:text-[38px]'
                        }
                    >
                        {companyName
                            ? t('employeePortal.objectives.hero.title', { company: companyName })
                            : t('employeePortal.objectives.hero.titleWithoutCompany')}
                    </h2>

                    <p
                        className={
                            'mt-3 max-w-xl leading-relaxed text-white/75 max-lg:text-[14px] lg:text-[16px]'
                        }
                    >
                        {t('employeePortal.objectives.hero.subtitle')}
                    </p>

                    {strategicVision && (
                        <p
                            className={'font-handwriting text-primary mt-6 text-[20px] italic'}
                            data-testid={'employee-portal-objectives-vision'}
                        >
                            {strategicVision}
                        </p>
                    )}
                </div>

                <div className={'relative shrink-0 max-lg:self-center'}>
                    {/* While the figures are unknown the ring is drawn empty but not announced: a
                        screen reader must not hear "zero percent" for an answer still on its way. */}
                    <ProgressRing
                        value={isSummaryKnown ? summary.overall : 0}
                        size={RING_SIZE_PX}
                        strokeWidth={RING_STROKE_PX}
                        colorClassName={'stroke-primary'}
                        trackClassName={'stroke-white/15'}
                        ariaHidden={!isSummaryKnown}
                        ariaLabel={overallLabel}
                        className={'rounded-full bg-white/10 ring-1 ring-white/20 backdrop-blur-xl'}
                        dataTestId={'employee-portal-objectives-overall'}
                    >
                        <div className={'flex flex-col items-center px-8 text-center'}>
                            <span
                                className={
                                    'text-[44px] leading-none font-semibold text-white tabular-nums'
                                }
                            >
                                {isSummaryKnown
                                    ? formatPercent(summary.overall, i18n.language)
                                    : UNKNOWN_FIGURE}
                            </span>

                            <span
                                className={
                                    'mt-1.5 text-[10px] leading-tight tracking-widest text-white/60 uppercase'
                                }
                            >
                                {overallLabel}
                            </span>
                        </div>
                    </ProgressRing>

                    {isSummaryKnown && (
                        <span
                            className={
                                'bg-primary text-navy absolute -bottom-2 left-1/2 inline-flex -translate-x-1/2 items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold whitespace-nowrap shadow-lg'
                            }
                        >
                            <Icon
                                name={'essential-flash'}
                                size={14}
                                color={'inherit'}
                                ariaHidden
                            />

                            {t('employeePortal.objectives.hero.inProgressCount', {
                                count: summary.onTrack + summary.atRisk,
                            })}
                        </span>
                    )}
                </div>
            </div>

            <div
                className={
                    'relative grid gap-px border-t border-white/15 bg-white/10 max-sm:grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
                }
            >
                {stats.map((stat) => (
                    <div
                        key={stat.key}
                        className={'flex items-center gap-4 bg-white/5 px-6 py-5'}
                        data-testid={`employee-portal-objectives-stat-${stat.key}`}
                    >
                        <span
                            aria-hidden
                            className={`${stat.tileClassName} flex size-[44px] items-center justify-center rounded-xl bg-linear-to-br`}
                        >
                            <Icon
                                name={stat.icon}
                                size={20}
                                colorClassName={stat.iconClassName}
                                ariaHidden
                            />
                        </span>

                        <div className={'flex flex-col gap-1'}>
                            <span
                                className={
                                    'text-[24px] leading-none font-semibold text-white tabular-nums'
                                }
                            >
                                {isSummaryKnown ? formatCount(stat.value) : UNKNOWN_FIGURE}
                            </span>
                            <span className={'text-[12px] text-white/65'}>{t(stat.labelKey)}</span>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
};
